import type { CountrySection, NamedCountry } from './countrySections'
import type { PhoneCountry } from './phoneCountries'

/** A heading, or one country under it. */
export type ListEntry =
  | {
      kind: 'header'
      key: string
      gap: boolean
      /** Whether the index rail lists it — the pinned heading is not a letter. */
      indexed: boolean
    }
  | {
      kind: 'row'
      key: string
      item: NamedCountry
      /** Which corners the row rounds, and whether it rules off below. */
      first: boolean
      last: boolean
    }

/** What a row or heading costs the list, so the rail can scrub by offset. */
export interface EntryHeights {
  row: number
  header: number
  /** What a `gap` entry adds above itself. */
  gap: number
}

/**
 * The sections flattened into rows a single list draws, with the country the
 * field already carries lifted to the top under its own heading (2184:14277).
 *
 * A search answers with one card and no headings (2531:8798): the letters are
 * how an unfiltered list is read, and over three matches they only add noise.
 */
export function countryListEntries(
  sections: readonly CountrySection[],
  query: string,
  pinned: PhoneCountry,
  /** What heads the pinned card — "Country:", in the reading language. */
  pinnedHeading: string,
): ListEntry[] {
  const needle = query.trim().toLowerCase()
  if (needle !== '') {
    const matches = ranked(
      sections.flatMap((section) => section.data),
      needle,
    )
    return matches.map((item, at) => ({
      kind: 'row',
      key: item.country.alpha2,
      item,
      first: at === 0,
      last: at === matches.length - 1,
    }))
  }

  const held = pinnedEntry(sections, pinned)
  // Lifted rather than copied: a country drawn twice is checked twice, and the
  // second one reads as a different country with the same name.
  const rest = withoutPinned(sections, pinned)
  return [
    ...(held === undefined
      ? []
      : [
          {
            kind: 'header' as const,
            key: pinnedHeading,
            gap: false,
            indexed: false,
          },
          held,
        ]),
    ...rest.flatMap((section, at) => [
      {
        kind: 'header' as const,
        key: section.key,
        gap: at > 0 || held !== undefined,
        indexed: true,
      },
      ...section.data.map((item, row) => ({
        kind: 'row' as const,
        key: item.country.alpha2,
        item,
        first: row === 0,
        last: row === section.data.length - 1,
      })),
    ]),
  ]
}

/** The selected country as its own card, named in the reading locale. */
function pinnedEntry(
  sections: readonly CountrySection[],
  pinned: PhoneCountry,
): ListEntry | undefined {
  const item = sections
    .flatMap((section) => section.data)
    .find(({ country }) => country.alpha2 === pinned.alpha2)
  if (item === undefined) {
    return undefined
  }
  return {
    kind: 'row',
    key: item.country.alpha2,
    item,
    first: true,
    last: true,
  }
}

function withoutPinned(
  sections: readonly CountrySection[],
  pinned: PhoneCountry,
): CountrySection[] {
  return sections
    .map((section) => ({
      key: section.key,
      data: section.data.filter(
        ({ country }) => country.alpha2 !== pinned.alpha2,
      ),
    }))
    .filter((section) => section.data.length > 0)
}

/** What answers the term, best answer first; ties keep the list's own order. */
function ranked(
  countries: readonly NamedCountry[],
  needle: string,
): NamedCountry[] {
  return countries
    .map((item) => ({ item, rank: matchRank(item, needle) }))
    .filter((scored) => scored.rank !== MATCHES_NOTHING)
    .sort((a, b) => a.rank - b.rank)
    .map(({ item }) => item)
}

/**
 * How well a country answers a typed term — the name, the ISO code it is
 * abbreviated by ("US" → United States), and the calling code, which is what a
 * caller knows when the name is not the one they use.
 *
 * An exact ISO code wins outright: it is the whole of what the user typed, and
 * "US" answered by Australia and Belarus first is the wrong answer to it. A
 * name that *starts* with the term beats one that merely contains it, for the
 * same reason.
 */
function matchRank({ country, name }: NamedCountry, needle: string): number {
  const alpha2 = country.alpha2.toLowerCase()
  const lower = name.toLowerCase()
  if (alpha2 === needle) {
    return 0
  }
  if (lower.startsWith(needle)) {
    return 1
  }
  if (alpha2.startsWith(needle)) {
    return 2
  }
  if (lower.includes(needle)) {
    return 3
  }
  return country.dialCode.includes(needle) ? 4 : MATCHES_NOTHING
}

/** Ranks worse than every match, so the filter drops it. */
const MATCHES_NOTHING = Number.POSITIVE_INFINITY

/**
 * The heading a list scrolled this far down is reading under, so the rail can
 * say where the list is. `undefined` above the first one, where the pinned
 * card is what is on screen and no letter has been reached.
 */
export function sectionAt(
  offsets: Record<string, number>,
  order: readonly string[],
  top: number,
): string | undefined {
  let held: string | undefined
  for (const key of order) {
    const offset = offsets[key]
    if (offset === undefined || offset > top) {
      break
    }
    held = key
  }
  return held
}

/** Where an entry sits in the list, and what it costs. */
export interface EntryCell {
  offset: number
  length: number
}

/** The list as the rail and the scroller both need it measured. */
export interface Measured {
  /** Every entry's place, so the list can be scrolled to one without
   * rendering its way there — a scrub crosses thousands of points at a time. */
  cells: EntryCell[]
  /** Where each letter heading sits, which the rail scrubs to. */
  offsets: Record<string, number>
  /** Those headings, in the order the list draws them. */
  order: string[]
  height: number
}

/**
 * One walk of the list, because a heading's offset, a row's cell and the whole
 * list's height are the same arithmetic — measured apart they drift, and the
 * rail would scrub to offsets the scroller does not honour.
 */
export function measureEntries(
  entries: readonly ListEntry[],
  heights: EntryHeights,
): Measured {
  const cells: EntryCell[] = []
  const offsets: Record<string, number> = {}
  const order: string[] = []
  let top = 0
  for (const entry of entries) {
    const gap = entry.kind === 'header' && entry.gap ? heights.gap : 0
    const own = entry.kind === 'header' ? heights.header : heights.row
    cells.push({ offset: top, length: gap + own })
    if (entry.kind === 'header' && entry.indexed) {
      // Below the gap that spaces it off the card above: the heading's own top
      // is where a scrub to it should land.
      offsets[entry.key] = top + gap
      order.push(entry.key)
    }
    top += gap + own
  }
  return { cells, offsets, order, height: top }
}
